import { useCallback, useRef, useState } from 'react';
import isEqual from 'react-fast-compare';

/**
 * Custom hook that creates local state from a prop,
 * and resets the state whenever the prop changes.
 * Optimized to avoid unnecessary state updates and re-renders.
 */
export const useComputedState = <T>(propValue: T) => {
  const [state, setStateInternal] = useState(propValue);
  const propRef = useRef(propValue);

  // Check if prop has changed and update state if necessary
  if (propRef.current !== propValue) {
    propRef.current = propValue;
    if (!isEqual(state, propValue)) {
      setStateInternal(propValue);
    }
  }

  // Memoized setState to prevent unnecessary re-renders of components using this hook
  const setState = useCallback(
    (newValue: T | ((prevState: T) => T)) => {
      const nextValue =
        typeof newValue === 'function'
          ? (newValue as (prevState: T) => T)(state)
          : newValue;
      if (isEqual(nextValue, state)) {
        return;
      }
      setStateInternal(nextValue);
    },
    [state]
  );

  return [state, setState] as const;
};

/**
 * Custom hook that creates local state from a prop,
 * and sets the state to the first non-null prop value.
 * After the first non-null value is set, subsequent prop changes are ignored.
 */
export const useComputedStateFirstNotNull = <T>(
  propValue: T | undefined,
  defaultvalue: T
) => {
  const [state, setStateInternal] = useState<T>(propValue ?? defaultvalue);
  const isSetRef = useRef(false);

  // Set state only on the first non-null prop value
  if (!isSetRef.current && propValue != null) {
    isSetRef.current = true;
    if (!isEqual(state, propValue)) {
      setStateInternal(propValue);
    }
  }

  // Memoized setState to prevent unnecessary re-renders of components using this hook
  const setState = useCallback(
    (newValue: T | ((prevState: T) => T)) => {
      const nextValue =
        typeof newValue === 'function'
          ? (newValue as (prevState: T) => T)(state)
          : newValue;
      if (isEqual(nextValue, state)) {
        return;
      }
      setStateInternal(nextValue);
    },
    [state]
  );

  return [state, setState] as const;
};
